namespace SolovisGateway.Admin.Organizations {

    // Named outcomes for ~/Organization/AddConnectionDomain and ~/Organization/RemoveConnectionDomain.
    // Mirror the backend's AddConnectionDomainOutcome / RemoveConnectionDomainOutcome enum member
    // names exactly (wire value = enum member name).
    export const AddConnectionDomainOutcome = {
        Added: "Added",
        AlreadyAdded: "AlreadyAdded",
        NotOrgDomain: "NotOrgDomain",
        DomainAlreadyLive: "DomainAlreadyLive",
        Auth0Error: "Auth0Error",
    } as const;
    export type AddConnectionDomainOutcome = typeof AddConnectionDomainOutcome[keyof typeof AddConnectionDomainOutcome];

    export const RemoveConnectionDomainOutcome = {
        Removed: "Removed",
        Auth0Error: "Auth0Error",
    } as const;
    export type RemoveConnectionDomainOutcome = typeof RemoveConnectionDomainOutcome[keyof typeof RemoveConnectionDomainOutcome];

    export interface OrgConnectionDomainsControlOptions extends Solovis.UI.BaseControlOptions, SupportContactOptions {
        connectionId: number;
        organizationId: number;
        connectionStatus: string;
        domains: Array<string>;
    }

    export class OrgConnectionDomainsControl extends Solovis.UI.BaseControl {

        private _connectionId: number;
        private _organizationId: number;
        private _connectionStatus: string;
        private _domainList: Array<string>;
        private _supportContact: SupportContactOptions;
        private _domainsTableBody: JQuery | null = null;
        private _addDomainLinkDiv: JQuery;
        private _addDomainFormDiv: JQuery;
        private _domainToAddInput: JQuery;
        private _alreadyAddedBanner: JQuery;
        private _errorBanner: JQuery;

        constructor(options: OrgConnectionDomainsControlOptions) {
            super(options);
            this._connectionId = options.connectionId;
            this._organizationId = options.organizationId;
            this._connectionStatus = options.connectionStatus;
            this._domainList = (options.domains || []).slice();
            this._supportContact = { supportContactText: options.supportContactText, supportContactLink: options.supportContactLink };
        }

        public getDomains(): Array<string> {
            return this._domainList.slice();
        }

        public doRender() {
            if (this._domainList.length > 0) {
                this._buildDomainsTable();
            }

            this._buildAddDomainForm();

            let helpText: string;
            if (this._connectionStatus === SsoConnectionStatus.Enabled) {
                helpText = "Adding or removing a domain on a live connection changes how its users sign in, so each is confirmed first.";
            } else if (this._connectionStatus === SsoConnectionStatus.Disabled) {
                helpText = "Domains on a disabled connection are not federated. They become federated only when you enable the connection.";
            } else {
                helpText = "Only the organization's own email domains can be added.";
            }

            $("<p>")
                .css({ "margin-top": "10px", "color": "#999" })
                .text(helpText)
                .appendTo(this.container);
        }

        private _buildDomainsTable() {
            let domainsTable = $("<table>")
                .css({ "margin-bottom": "0", "width": "100%" })
                .addClass("table table-striped table-condensed")
                .appendTo(this.container);

            let domainsTableHead = $("<thead>").appendTo(domainsTable);
            $("<tr>")
                .appendTo(domainsTableHead)
                .append($("<th>").text("Email Domain"))
                .append($("<th>").text("Actions").css({ "text-align": "center", "width": "80px" }));

            this._domainsTableBody = $("<tbody>").appendTo(domainsTable);

            for (let i = 0; i < this._domainList.length; i++) {
                this._appendDomainRow(this._domainList[i]);
            }
        }

        private _appendDomainRow(domain: string) {
            let self = this;
            let row = $("<tr>").appendTo(this._domainsTableBody);
            $("<td>").css("vertical-align", "middle").text(domain).appendTo(row);

            let removeDomainButton = new Solovis.UI.Buttons.Button({
                element: $("<span>").addClass("glyphicon glyphicon-trash"),
                tooltipOptions: {
                    text: "Remove Email Domain " + domain
                },
                size: "small",
                click: function () {
                    self._confirmRemoveDomain(domain);
                }
            });

            $("<td>")
                .css({ "text-align": "center", "width": "80px", "vertical-align": "middle" })
                .append(removeDomainButton.getElement())
                .appendTo(row);
        }

        private _buildAddDomainForm() {
            let self = this;

            let showAddDomainUIButton = new Solovis.UI.Buttons.Button({
                element: $("<div>").text("Add Email Domain").prepend($("<span>").css("padding-right", "5px").addClass("fa fa-plus")),
                size: "small",
                click: function () {
                    self._showAddDomainUI();
                }
            });

            this._addDomainLinkDiv = $("<div>")
                .css("padding-top", "5px")
                .append(showAddDomainUIButton.getElement())
                .appendTo(this.container);

            this._addDomainFormDiv = $("<div>")
                .css({ "padding-top": "5px", "display": "none" })
                .appendTo(this.container);

            $("<span>").text("Email Domain").appendTo(this._addDomainFormDiv);

            this._alreadyAddedBanner = $("<div>").css({
                "background-color": "#fcf8e3",
                "border": "1px solid #faebcc",
                "color": "#8a6d3b",
                "padding": "8px 10px",
                "border-radius": "3px",
                "margin": "8px 0",
                "display": "none"
            }).appendTo(this._addDomainFormDiv);

            this._errorBanner = $("<div>").css({
                "background-color": "#f2dede",
                "border": "1px solid #ebccd1",
                "color": "#a94442",
                "padding": "8px 10px",
                "border-radius": "3px",
                "margin": "8px 0",
                "display": "none"
            }).appendTo(this._addDomainFormDiv);

            this._domainToAddInput = $("<input>")
                .css({ "height": "32px", "margin": "0 5px" })
                .appendTo(this._addDomainFormDiv);

            let addTypedInDomainButton = new Solovis.UI.Buttons.Button({
                text: "Add",
                click: function () {
                    self._addTypedInDomain(addTypedInDomainButton.getElement());
                },
                cssRules: {
                    "margin-bottom": "5px"
                }
            });

            let hideAddDomainUIButton = new Solovis.UI.Buttons.Button({
                text: "Cancel",
                click: function () {
                    self._hideAddDomainUI();
                },
                cssRules: {
                    "margin-bottom": "5px"
                }
            });

            this._addDomainFormDiv
                .append(addTypedInDomainButton.getElement())
                .append(hideAddDomainUIButton.getElement());
        }

        private _showAddDomainUI() {
            this._addDomainLinkDiv.hide();
            this._addDomainFormDiv.fadeIn();
            this._domainToAddInput.focus();
        }

        private _hideAddDomainUI() {
            this._addDomainFormDiv.hide();
            this._addDomainLinkDiv.fadeIn();
            this._domainToAddInput.val("");
            this._alreadyAddedBanner.hide();
            this._errorBanner.hide();
            this._domainToAddInput.css({ "border-color": "" });
        }

        private async _addTypedInDomain(addButtonElement: JQuery) {
            let domain = (this._domainToAddInput.val() as string || "").trim();

            this._alreadyAddedBanner.hide();
            this._errorBanner.hide();

            if (!domain) {
                return;
            }

            if (!this._isValidDomain(domain)) {
                this._errorBanner.text("Enter a valid domain, like example.com.").show();
                this._domainToAddInput.css({ "border-color": "#a94442" });
                return;
            }

            if (this._connectionStatus === SsoConnectionStatus.Enabled) {
                this._confirmAddDomainToLiveConnection(domain);
                return;
            }

            addButtonElement.prop("disabled", true);
            this._domainToAddInput.prop("disabled", true);
            this._domainToAddInput.css({ "border-color": "" });

            try {
                let response = await Solovis.Rpc.invoke("~/Organization/AddConnectionDomain", {
                    connectionId: this._connectionId,
                    organizationId: this._organizationId,
                    domain: domain
                });

                let outcome = response.result.outcome;

                if (outcome === AddConnectionDomainOutcome.Added) {
                    location.reload();
                    return;
                }

                if (outcome === AddConnectionDomainOutcome.AlreadyAdded) {
                    this._alreadyAddedBanner.text(domain + " is already on this connection.").show();
                }
                else if (outcome === AddConnectionDomainOutcome.NotOrgDomain) {
                    this._errorBanner.empty();
                    this._errorBanner.append($("<span>").text("Only the organization's email domains can be added. "));
                    SsoConnectionStatusUi.appendSupportContactLine(
                        this._errorBanner, this._supportContact, "To add a new organization domain, ", "."
                    );
                    this._errorBanner.show();
                    this._domainToAddInput.css({ "border-color": "#a94442" });
                }
            }
            catch (e) {
                this._errorBanner.empty();
                this._errorBanner.append($("<span>").text("We could not add the email domain. Please try again in a little while. "));
                SsoConnectionStatusUi.appendSupportContactLine(
                    this._errorBanner, this._supportContact, "If the problem continues, ", "."
                );
                this._errorBanner.show();
            }
            finally {
                addButtonElement.prop("disabled", false);
                this._domainToAddInput.prop("disabled", false);
            }
        }

        private _isValidDomain(domain: string): boolean {
            let domainPattern = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
            return domainPattern.test(domain);
        }

        private _confirmAddDomainToLiveConnection(domain: string) {
            let self = this;

            let addDialog = new SolovisGateway.Admin.Organizations.OrgAddDomainToLiveConnectionDialog({
                domain: domain,
                supportContactText: self._supportContact.supportContactText,
                supportContactLink: self._supportContact.supportContactLink,
                onAdd: async function () {
                    try {
                        let response = await Solovis.Rpc.invoke("~/Organization/AddConnectionDomain", {
                            connectionId: self._connectionId,
                            organizationId: self._organizationId,
                            domain: domain
                        });

                        let outcome = response.result.outcome;

                        if (outcome === AddConnectionDomainOutcome.Added) {
                            location.reload();
                            return;
                        }

                        if (outcome === AddConnectionDomainOutcome.AlreadyAdded) {
                            addDialog.showAlreadyAdded();
                        }
                        else if (outcome === AddConnectionDomainOutcome.NotOrgDomain) {
                            addDialog.showError([
                                $("<span>").text("Only the organization's email domains can be added. "),
                                ...SsoConnectionStatusUi.buildSupportContactNodes(
                                    self._supportContact, "To add a new organization domain, ", "."
                                )
                            ]);
                        }
                        else if (outcome === AddConnectionDomainOutcome.DomainAlreadyLive) {
                            addDialog.showError(
                                domain + " is already live on a different connection. Disable or remove it from that connection first."
                            );
                        }
                        else {
                            addDialog.showError([
                                $("<span>").text("We could not add the email domain. Please try again in a little while. "),
                                ...SsoConnectionStatusUi.buildSupportContactNodes(
                                    self._supportContact, "If the problem continues, ", "."
                                )
                            ]);
                        }
                    }
                    catch (e) {
                        addDialog.showError([
                            $("<span>").text("We could not add the email domain. Please try again in a little while. "),
                            ...SsoConnectionStatusUi.buildSupportContactNodes(
                                self._supportContact, "If the problem continues, ", "."
                            )
                        ]);
                    }
                }
            });
            addDialog.show();
        }

        private _confirmRemoveDomain(domain: string) {
            if (this._connectionStatus === SsoConnectionStatus.Enabled) {
                this._confirmRemoveDomainFromLiveConnection(domain);
                return;
            }

            let self = this;
            let removeDialog = new SolovisGateway.Admin.Organizations.OrgRemoveDomainDialog({
                domain: domain,
                onRemove: async function () {
                    try {
                        let response = await Solovis.Rpc.invoke("~/Organization/RemoveConnectionDomain", {
                            connectionId: self._connectionId,
                            organizationId: self._organizationId,
                            domain: domain
                        });

                        if (response.result.outcome === RemoveConnectionDomainOutcome.Removed) {
                            location.reload();
                            return;
                        }
                        else {
                            removeDialog.resetToConfirm();
                            Solovis.UI.alert(
                                "We could not remove the email domain. Please try again.",
                                "Unable to remove domain"
                            );
                        }
                    }
                    catch (e) {
                        removeDialog.resetToConfirm();
                        Solovis.UI.alert(
                            "We could not remove the email domain. Please try again.",
                            "Unable to remove domain"
                        );
                    }
                }
            });
            removeDialog.show();
        }

        private _confirmRemoveDomainFromLiveConnection(domain: string) {
            let self = this;
            let isLastDomain = this._domainList.length === 1;

            let removeDialog = new SolovisGateway.Admin.Organizations.OrgRemoveDomainFromLiveConnectionDialog({
                domain: domain,
                isLastDomain: isLastDomain,
                onRemove: async function () {
                    try {
                        let response = await Solovis.Rpc.invoke("~/Organization/RemoveConnectionDomain", {
                            connectionId: self._connectionId,
                            organizationId: self._organizationId,
                            domain: domain
                        });

                        if (response.result.outcome === RemoveConnectionDomainOutcome.Removed) {
                            location.reload();
                            return;
                        }

                        removeDialog.resetToConfirm();
                        Solovis.UI.alert(
                            "We could not remove the email domain. Please try again.",
                            "Unable to remove domain"
                        );
                    }
                    catch (e) {
                        removeDialog.resetToConfirm();
                        Solovis.UI.alert(
                            "We could not remove the email domain. Please try again.",
                            "Unable to remove domain"
                        );
                    }
                }
            });
            removeDialog.show();
        }
    }
}
